/**
 * Stripe Helpers — Frontend (web version)
 *
 * Calls Cloudflare Functions for server-side Stripe operations.
 * No Stripe SDK needed on the frontend — just fetch calls.
 */

import { debug } from './debug.js';

/**
 * Create a Stripe Checkout Session and redirect to it.
 * @param {string} packId - Cash pack ID (e.g. 'expansion_fund')
 * @param {string} userId - Supabase user ID
 * @returns {Promise<{url?: string, error?: string}>}
 */
export async function createCheckout(packId, userId) {
  try {
    const res = await fetch('/api/stripe-checkout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pack_id: packId, user_id: userId }),
    });

    const data = await res.json();

    if (data.error) {
      debug('[Stripe] Checkout error:', data.error);
      return { error: data.error };
    }

    if (data.url) {
      debug('[Stripe] Redirecting to Stripe Checkout:', data.url);
      window.location.href = data.url;
    }

    return data;
  } catch (err) {
    debug('[Stripe] createCheckout failed:', err);
    return { error: err.message };
  }
}

/**
 * Verify a completed Stripe Checkout Session and get the grant.
 * @param {string} sessionId - Stripe Checkout Session ID (cs_xxx)
 * @param {string} userId - Supabase user ID
 * @returns {Promise<{success: boolean, grant?: {cash: number, wings: number, pack_name: string}, error?: string}>}
 */
export async function verifyPurchase(sessionId, userId) {
  try {
    const res = await fetch('/api/stripe-verify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_id: sessionId, user_id: userId }),
    });

    const data = await res.json();
    debug('[Stripe] Verify result:', data);
    return data;
  } catch (err) {
    debug('[Stripe] verifyPurchase failed:', err);
    return { success: false, error: err.message };
  }
}

/**
 * Check URL for session_id param (from Stripe redirect back).
 * @returns {string|null}
 */
export function getSessionIdFromUrl() {
  const params = new URLSearchParams(window.location.search);
  return params.get('session_id');
}

/**
 * Clean up session_id from URL after processing.
 */
export function clearSessionIdFromUrl() {
  const url = new URL(window.location.href);
  url.searchParams.delete('session_id');
  window.history.replaceState(null, '', url.pathname);
}
